import { t } from "../i18n/index.js";
import "./netStatus.css";

/**
 * Chivato de red y rendimiento durante un partido online.
 *
 * Existe por una razon muy concreta: cuando alguien dice "ayer con mis amigos
 * las salas no iban del todo bien y en un portatil laguearse", no hay forma de
 * saber QUE paso. "Lag" describe la sensacion, no la causa, y las tres causas
 * posibles se arreglan en sitios distintos:
 *
 *  - **Ping alto**: la culpa es del viaje. El margen de input del lockstep sube
 *    solo para cubrirlo, asi que se juega, pero con retardo entre pulsar y ver.
 *  - **FPS bajos**: la culpa es del ordenador. La red va perfecta y aun asi se
 *    ve a tirones, porque la fisica avanza a saltos entre dibujo y dibujo. Es
 *    exactamente lo que le pasa a un portatil justo, y no se parece en nada al
 *    problema anterior aunque se sienta igual.
 *  - **Cola (`atrasado`)**: ticks que han llegado y aun no se han simulado. Si
 *    crece, este cliente va por detras del partido de los demas.
 *
 * Con los tres numeros en pantalla, la siguiente prueba con amigos deja de ser
 * una impresion y pasa a ser un dato.
 *
 * Se pinta en HTML y no en el lienzo a proposito: no puede competir por tiempo
 * de GPU justo con lo que esta midiendo.
 */

export interface NetSample {
  /** Ida y vuelta con el servidor, en ms. -1 si aun no hay medida. */
  rttMs: number;
  /** Media de ms por frame. -1 si aun no hay medida. */
  frameMs: number;
  /** Ticks recibidos y todavia sin simular. */
  backlog: number;
  /** Margen de input que el lockstep esta aplicando, en ticks. */
  lead: number;
  /** El partido ha divergido: ya no todos ven lo mismo. */
  desynced: boolean;
  /**
   * Hay una repeticion en pantalla.
   *
   * El chivato se aparta: comparte esquina con la chapa de REPLAY, y ademas los
   * numeros no significan nada ahi — durante el clip no se esta jugando, asi
   * que unos fps bajos no serian el sintoma de nada.
   */
  replaying: boolean;
}

export interface NetStatus {
  update(sample: NetSample): void;
  show(): void;
  hide(): void;
  dispose(): void;
}

/** Por encima de esto, el retardo entre pulsar y ver ya se nota. */
const PING_WARN = 90;
const PING_BAD = 170;
/** 22 ms ≈ 45 fps: por debajo el juego deja de verse continuo. */
const FRAME_WARN = 22;
const FRAME_BAD = 33;
/** Media docena de ticks de cola es medio pestañeo; treinta ya es ir detras. */
const BACKLOG_WARN = 10;
const BACKLOG_BAD = 30;

function grade(value: number, warn: number, bad: number): string {
  if (value >= bad) return "is-bad";
  if (value >= warn) return "is-warn";
  return "is-ok";
}

export function createNetStatus(root: HTMLElement = document.body): NetStatus {
  const el = document.createElement("div");
  el.id = "net-status";
  el.className = "is-hidden";
  el.setAttribute("aria-live", "off");
  el.innerHTML = `
    <span class="net-status__item" data-ping><b>—</b><i>ms</i></span>
    <span class="net-status__item" data-fps><b>—</b><i>fps</i></span>
    <span class="net-status__item is-quiet" data-lag hidden><b>0</b><i>${t("cola")}</i></span>
  `;
  root.appendChild(el);

  const ping = el.querySelector<HTMLElement>("[data-ping]")!;
  const fps = el.querySelector<HTMLElement>("[data-fps]")!;
  const lag = el.querySelector<HTMLElement>("[data-lag]")!;

  const setItem = (item: HTMLElement, value: string, cls: string): void => {
    const b = item.querySelector("b")!;
    if (b.textContent !== value) b.textContent = value;
    item.classList.remove("is-ok", "is-warn", "is-bad");
    item.classList.add(cls);
  };

  /*
   * Se refresca cuatro veces por segundo, no en cada frame.
   *
   * Un numero que cambia 60 veces por segundo es ilegible —el ojo solo ve un
   * borron— y encima cada cambio de texto es un reflow del navegador, o sea que
   * el chivato empeoraria lo que mide.
   */
  let nextAt = 0;

  return {
    update(sample: NetSample): void {
      // Le deja la esquina a la chapa de REPLAY, que ocupa el mismo sitio.
      el.classList.toggle("is-hidden", sample.replaying);
      if (sample.replaying) return;

      const now = performance.now();
      if (now < nextAt) return;
      nextAt = now + 250;

      const rtt = Math.max(0, Math.round(sample.rttMs));
      setItem(ping, sample.rttMs < 0 ? "—" : String(rtt), grade(rtt, PING_WARN, PING_BAD));

      const frame = sample.frameMs;
      setItem(
        fps,
        frame <= 0 ? "—" : String(Math.round(1000 / frame)),
        grade(frame <= 0 ? 0 : frame, FRAME_WARN, FRAME_BAD),
      );

      // La cola solo aparece cuando hay cola. En una conexion sana es cero
      // siempre, y un cero permanente es ruido que enseña a no mirar el chivato.
      lag.hidden = sample.backlog < BACKLOG_WARN;
      if (!lag.hidden) {
        setItem(lag, String(sample.backlog), grade(sample.backlog, BACKLOG_WARN, BACKLOG_BAD));
      }

      el.classList.toggle("is-desynced", sample.desynced);
    },
    show(): void {
      el.classList.remove("is-hidden");
    },
    hide(): void {
      el.classList.add("is-hidden");
    },
    dispose(): void {
      el.remove();
    },
  };
}
